import React, { useState, useRef, useEffect } from 'react'
import { CHAT_KEYFRAMES } from './keyframes'
import ChatInputBar from './ChatInputBar'

// Reusable chat widget chrome: a floating "PropStream Intelligence" launcher
// pill and a toggled panel (indigo header + scrollable message area + input
// bar). The panel is draggable (by the header) and resizable (top-left corner),
// mirroring the property-details assistant so both stay identical.
//
// Message rendering is the caller's job — pass rendered items as children. The
// shell owns the keyframes, launcher, panel, header, drag/resize, scroll
// container, bottom sentinel (endRef), and input bar.
const DEFAULT_POS = { right: 20, bottom: 0 }
const DEFAULT_SIZE = { width: 390, height: 540 }
const MIN_SIZE = { width: 320, height: 400 }

export default function ChatShell({
  open,
  onToggle,
  titleLine1 = 'PropStream',
  titleLine2 = 'Intelligence',
  loading,
  inputValue,
  onInputChange,
  onSend,
  onStop,
  placeholder,
  inputRef,
  endRef,
  children,
}) {
  const [panelPos, setPanelPos] = useState(DEFAULT_POS)
  const [panelSize, setPanelSize] = useState(DEFAULT_SIZE)

  // Reset position/size each time the panel opens.
  useEffect(() => {
    if (open) { setPanelPos(DEFAULT_POS); setPanelSize(DEFAULT_SIZE) }
  }, [open])

  const handleDragStart = (e) => {
    if (e.target.closest('button') || e.target.closest('input')) return
    e.preventDefault()
    const startX = e.clientX
    const startY = e.clientY
    const startRight = panelPos.right
    const startBottom = panelPos.bottom
    const curW = panelSize.width
    const curH = panelSize.height
    const onMove = (ev) => {
      const dx = ev.clientX - startX
      const dy = ev.clientY - startY
      const vw = window.innerWidth
      const vh = window.innerHeight
      setPanelPos({
        right: Math.min(vw - curW, Math.max(0, startRight - dx)),
        bottom: Math.min(vh - curH, Math.max(0, startBottom - dy)),
      })
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const handleResizeStart = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startY = e.clientY
    const startW = panelSize.width
    const startH = panelSize.height
    const startRight = panelPos.right
    const startBottom = panelPos.bottom
    const onMove = (ev) => {
      const dx = ev.clientX - startX
      const dy = ev.clientY - startY
      const vw = window.innerWidth
      const vh = window.innerHeight
      const maxW = vw - startRight
      const maxH = vh - startBottom
      const newW = Math.min(maxW, Math.max(MIN_SIZE.width, startW - dx))
      const newH = Math.min(maxH, Math.max(MIN_SIZE.height, startH - dy))
      setPanelSize({ width: newW, height: newH })
      setPanelPos((prev) => ({ ...prev, right: startRight }))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  return (
    <>
      <style>{CHAT_KEYFRAMES}</style>

      {/* Floating pill launcher (always visible) */}
      <button
        onClick={onToggle}
        aria-label="Toggle PropStream Intelligence assistant"
        style={{
          all: 'unset', boxSizing: 'border-box', position: 'fixed',
          bottom: 24, right: 20, zIndex: 10001,
          backgroundColor: '#5B68C3', border: 'none', borderRadius: 999,
          padding: '9px 14px', cursor: 'pointer',
          display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1,
          height: 38, maxWidth: 164, fontFamily: 'Inter, sans-serif',
          boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.05), 0px 10px 15px 0px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 3 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ animation: 'psStarSpin 2s ease-in-out infinite', flexShrink: 0 }}>
            <path d="M9.71757 0.555288L12.0974 5.67187L17.214 8.05168C17.5313 8.21033 17.7692 8.52762 17.7692 8.88462C17.7692 9.28129 17.5313 9.5986 17.214 9.75717L12.0974 12.1371L9.71757 17.2536C9.55892 17.5709 9.24161 17.7692 8.88462 17.7692C8.48794 17.7692 8.17067 17.5709 8.01202 17.2536L5.63221 12.1371L0.515625 9.75717C0.198317 9.5986 0 9.28129 0 8.88462C0 8.52762 0.198317 8.21033 0.515625 8.05168L5.63221 5.67187L8.01202 0.555288C8.17067 0.237981 8.48794 0 8.88462 0C9.24161 0 9.55892 0.237981 9.71757 0.555288Z" fill="white"/>
          </svg>
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: -2 }}>
            <span style={{ fontSize: 11, fontWeight: 600, lineHeight: '13px', color: '#fff', whiteSpace: 'nowrap' }}>{titleLine1}</span>
            <span style={{ fontSize: 16, fontWeight: 600, lineHeight: '15px', color: '#fff', whiteSpace: 'nowrap' }}>{titleLine2}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 8, flexShrink: 0 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="9" viewBox="0 0 15 9" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
            <path d="M6.80419 0.287393C7.18739 -0.0957978 7.80701 -0.0957978 8.18613 0.287393L14.7126 6.8098C15.0958 7.19299 15.0958 7.81262 14.7126 8.19173C14.3294 8.57085 13.7098 8.57492 13.3307 8.19173L7.50128 2.36233L1.6678 8.19581C1.28461 8.579 0.664981 8.579 0.285867 8.19581C-0.0932479 7.81262 -0.0973244 7.19299 0.285867 6.81387L6.80419 0.287393Z" fill="#E8EBFC"/>
          </svg>
        </div>
      </button>

      {/* Panel */}
      {open && (
        <div style={{
          position: 'fixed', bottom: panelPos.bottom, right: panelPos.right, zIndex: 10001,
          width: panelSize.width, height: panelSize.height, display: 'flex', flexDirection: 'column',
          borderRadius: 14, overflow: 'hidden',
          boxShadow: '0px 10px 10px 0px rgba(0, 0, 0, 0.04), 0px 20px 25px 0px rgba(0, 0, 0, 0.1)',
          fontFamily: 'Inter, sans-serif', border: '1px solid rgba(27,58,107,0.15)',
        }}>
          {/* Resize handle (top-left corner) */}
          <div
            onMouseDown={handleResizeStart}
            title="Drag to resize"
            style={{ position: 'absolute', top: 4, left: 4, width: 20, height: 20, cursor: 'nw-resize', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
              <circle cx="1.5" cy="1.5" r="1.5" fill="#AFB6E5"/>
              <circle cx="1.5" cy="6.5" r="1.5" fill="#AFB6E5"/>
              <circle cx="1.5" cy="11.5" r="1.5" fill="#AFB6E5"/>
              <circle cx="6.5" cy="1.5" r="1.5" fill="#AFB6E5"/>
              <circle cx="6.5" cy="6.5" r="1.5" fill="#AFB6E5"/>
              <circle cx="11.5" cy="1.5" r="1.5" fill="#AFB6E5"/>
            </svg>
          </div>

          {/* Header (draggable) */}
          <div
            onMouseDown={handleDragStart}
            style={{
              boxSizing: 'border-box', background: '#5B68C3', padding: '9px 16px 9px 30px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
              borderRadius: '12px 12px 0 0',
              boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.05), 0px 10px 15px 0px rgba(0, 0, 0, 0.1)',
              cursor: 'grab', userSelect: 'none', height: 52,
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {/* Drag grip */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, opacity: 0.6 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="23" height="14" viewBox="0 0 23 14" fill="none">
                  <circle cx="2.5" cy="2.5" r="2.5" fill="#AFB6E5"/>
                  <circle cx="11.5" cy="2.5" r="2.5" fill="#AFB6E5"/>
                  <circle cx="20.5" cy="2.5" r="2.5" fill="#AFB6E5"/>
                  <circle cx="2.5" cy="11.5" r="2.5" fill="#AFB6E5"/>
                  <circle cx="11.5" cy="11.5" r="2.5" fill="#AFB6E5"/>
                  <circle cx="20.5" cy="11.5" r="2.5" fill="#AFB6E5"/>
                </svg>
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M11.4844 0.656051L14.2969 6.70293L20.3438 9.51543C20.7188 9.70293 21 10.0779 21 10.4998C21 10.9686 20.7188 11.3436 20.3438 11.531L14.2969 14.3436L11.4844 20.3904C11.2969 20.7654 10.9219 20.9998 10.5 20.9998C10.0312 20.9998 9.65625 20.7654 9.46875 20.3904L6.65625 14.3436L0.609375 11.531C0.234375 11.3436 0 10.9686 0 10.4998C0 10.0779 0.234375 9.70293 0.609375 9.51543L6.65625 6.70293L9.46875 0.656051C9.65625 0.281051 10.0312 -0.000199318 10.5 -0.000199318C10.9219 -0.000199318 11.2969 0.281051 11.4844 0.656051Z" fill="white"/>
                </svg>
                <div style={{ display: 'flex', flexDirection: 'column', marginTop: -2 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, lineHeight: '15.8px', color: '#fff', whiteSpace: 'nowrap' }}>{titleLine1}</span>
                  <span style={{ fontSize: 20, fontWeight: 600, lineHeight: '20px', color: '#fff', whiteSpace: 'nowrap' }}>{titleLine2}</span>
                </div>
              </div>
            </div>
            <button
              onClick={onToggle}
              aria-label="Close assistant"
              style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 30 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="9" viewBox="0 0 15 9" fill="none">
                <path d="M8.19581 8.196C7.81261 8.5792 7.19299 8.5792 6.81387 8.196L0.287393 1.6736C-0.0957979 1.29041 -0.0957978 0.670781 0.287393 0.291667C0.670584 -0.0874484 1.29021 -0.0915244 1.66933 0.291667L7.49872 6.12107L13.3322 0.287591C13.7154 -0.0956002 14.335 -0.0956002 14.7141 0.287591C15.0932 0.670782 15.0973 1.29041 14.7141 1.66952L8.19581 8.196Z" fill="#E8EBFC"/>
              </svg>
            </button>
          </div>

          {/* Message list */}
          <div style={{ flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'auto', background: '#EFF2F6', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, padding: '18px 12px' }}>
              {children}
              <div ref={endRef} />
            </div>
          </div>

          {/* Input */}
          <ChatInputBar
            value={inputValue}
            onChange={onInputChange}
            onSend={onSend}
            onStop={onStop}
            loading={loading}
            placeholder={placeholder}
            inputRef={inputRef}
          />
        </div>
      )}
    </>
  )
}
